"use client"

import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { useAppDispatch, useAppSelector } from "@/store/hooks"
import { loadUserMenus } from "@/store/slices/authSlice"
import { setActiveTab } from "@/store/slices/navigationSlice"
import {
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Clock,
  Pin,
  PinOff,
  RefreshCw,
  Search,
} from "lucide-react"
import type { MenuItem } from "@/types/menu"
import { getIconComponent, getGroupIconComponent } from "@/utils/iconMapper"
import { useAuthContext } from "@/contexts/AuthContext"
import { useSidebar } from "@/components/ui/sidebar"
import { SidebarClockFooter } from "@/features/attendance-clock"
import {
  deriveNavGroups,
  filterNavGroups,
  findActiveGroupId,
  findActiveItemId,
  isGroupOpen,
  normalizePath,
  reduceHeadClick,
  resolvePinnedItems,
  showActiveDot,
  sumGroupBadges,
  type NavGroup,
} from "@/components/sidebar/nav-model"
import { useLocale, pickI18n } from "@/hooks/useLocale"
import {
  useMenuBadges,
  MY_TASKS_BADGE_KEY,
  VOICEMAIL_BADGE_KEY,
  OWNER_ACTIVITY_BADGE_KEY,
} from "./sidebar/useMenuBadges"
import { MenuBranches } from "@/components/sidebar/MenuBranches"
import { Collapse } from "@/components/sidebar/Collapse"
import { usePinnedNav } from "@/components/sidebar/usePinnedNav"
import { resolveMenuEmptyKind } from "@/components/sidebar/menuEmptyState"
import "@/components/sidebar/app-sidebar.css"

// 侧栏完全由后端 role→menu(state.auth.menuTree)驱动,前端不做权限过滤。
// UI 复刻 Claude Design handoff「Nav Menu UI」最终方案(Frank 2026-06-12 简化版):
// 展开态 = My Workspace 钉住 + 滚动区单组手风琴(同时只开一组,active 组被关时
// 标题出琥珀点); 菜单按内容高度顺序排列;
// 收起态(D4) = 64px 图标 rail,hover 滑出带文字弹窗,点选后原地展开落位;
// + 菜单搜索(不占快捷键:⌘K 归顶栏的全局搜索,Frank 2026-10-23)。

const PINNED_OPEN_KEY = "sidebar:pinned-open"

const loadPinnedOpen = (): boolean => {
  try {
    return localStorage.getItem(PINNED_OPEN_KEY) !== "false"
  } catch {
    return true
  }
}

/** 搜索命中高亮（不区分大小写的第一处子串） */
const Highlight = ({ text, query }: { text: string; query: string }) => {
  if (!query) return <span>{text}</span>
  const i = text.toLowerCase().indexOf(query)
  if (i < 0) return <span>{text}</span>
  return (
    <span>
      {text.slice(0, i)}
      <mark>{text.slice(i, i + query.length)}</mark>
      {text.slice(i + query.length)}
    </span>
  )
}

const SearchBox = ({
  q,
  setQ,
  inputRef,
  placeholder,
  ariaLabel,
  clearTitle,
}: {
  q: string
  setQ: (v: string) => void
  inputRef: React.RefObject<HTMLInputElement | null>
  placeholder: string
  ariaLabel: string
  clearTitle: string
}) => (
  <div className="nv-sbx">
    <span className="nv-sbx-ico">
      <Search size={15} strokeWidth={2} />
    </span>
    <input
      ref={inputRef}
      value={q}
      onChange={(e) => setQ(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Escape") setQ("")
      }}
      placeholder={placeholder}
      aria-label={ariaLabel}
    />
    {q && (
      <button type="button" className="nv-sbx-clear" onClick={() => setQ("")} title={clearTitle}>
        ×
      </button>
    )}
  </div>
)

/**
 * 收起态图标 rail（handoff §05 · D4）：默认只显示分组图标（仅 active 组初始原地展开）。
 * hover 分组图标 → 右侧滑出带文字的弹出菜单（100–150ms 开启延迟,靠下的组向上锚定）；
 * 在弹窗里点选 → 跳转 + 弹窗关闭 + 该组在 rail 上原地展开小图标（手风琴单组）落位。
 */
const Rail = ({
  groups,
  activeItemId,
  activeGroupId,
  onNavigate,
  onExpand,
  onSearch,
  expandTip,
  searchTip,
  adminLabel,
  offLabel,
}: {
  groups: NavGroup[]
  activeItemId: string | null
  activeGroupId: string | null
  onNavigate: (item: MenuItem) => void
  onExpand: () => void
  onSearch: () => void
  expandTip: string
  searchTip: string
  adminLabel: string
  offLabel: string
}) => {
  const locale = useLocale()
  // 初始进入 rail 时默认展开「当前选中项所在的组」
  const [openGroup, setOpenGroup] = useState<string | null>(activeGroupId)
  const [flyGroup, setFlyGroup] = useState<string | null>(null)
  const flyTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // hover 开启延迟 120ms（鼠标快速划过不闪菜单）;离开立即关
  const enterGroup = (id: string) => {
    if (flyTimer.current) clearTimeout(flyTimer.current)
    flyTimer.current = setTimeout(() => setFlyGroup(id), 120)
  }
  const leaveGroup = () => {
    if (flyTimer.current) clearTimeout(flyTimer.current)
    flyTimer.current = null
    setFlyGroup(null)
  }
  useEffect(() => () => {
    if (flyTimer.current) clearTimeout(flyTimer.current)
  }, [])

  return (
    <div className="nv-rail">
      <button type="button" className="nv-rail-btn" data-tip={expandTip} onClick={onExpand}>
        <ChevronsRight size={17} strokeWidth={2} />
      </button>
      <button type="button" className="nv-rail-btn" data-tip={searchTip} onClick={onSearch}>
        <Search size={17} strokeWidth={2} />
      </button>
      <div className="nv-rail-sep" />
      {groups.map((g, idx) => {
        const open = openGroup === g.id
        const GroupIcon = getGroupIconComponent(g.label, g.icon)
        return (
          <div
            key={g.id}
            className="nv-rail-grp"
            data-open={open}
            onMouseEnter={() => enterGroup(g.id)}
            onMouseLeave={leaveGroup}
          >
            <button
              type="button"
              className="nv-rail-btn nv-rail-gbtn"
              data-active={g.id === activeGroupId && !open}
              aria-label={g.label}
              onClick={() => setOpenGroup(open ? null : g.id)}
            >
              <GroupIcon size={18} strokeWidth={1.7} />
            </button>
            <Collapse open={open}>
              <div className="nv-rail-items">
                {g.items.map((it) => {
                  const ItemIcon = getIconComponent(it.icon, it.routePath)
                  const disabled = it.isActive === false
                  return (
                    <button
                      key={it.id}
                      type="button"
                      className="nv-rail-ibtn"
                      data-active={String(it.id) === activeItemId}
                      data-disabled={disabled}
                      data-tip={pickI18n(it.labelI18n, locale, it.name) + (disabled ? " · " + offLabel : "")}
                      onClick={() => !disabled && onNavigate(it)}
                    >
                      <ItemIcon size={15} strokeWidth={1.7} />
                    </button>
                  )
                })}
              </div>
            </Collapse>
            {flyGroup === g.id && (
              // 靠下的组（第 6 个起）弹窗向上锚定防溢出
              <div className="nv-fly" data-up={idx >= 5}>
                <div className="nv-fly-card">
                  <div className="nv-fly-head">
                    <span>{pickI18n(g.labelI18n, locale, g.label)}</span>
                    {g.adminOnly && <span className="nv-admin">{adminLabel}</span>}
                  </div>
                  <MenuBranches items={g.tree ?? g.items} activeItemId={activeItemId} searching={false} renderLeaf={(it) => {
                    const disabled = it.isActive === false
                    return (
                      <button
                        key={it.id}
                        type="button"
                        className="nv-fly-item"
                        data-active={String(it.id) === activeItemId}
                        data-disabled={disabled}
                        disabled={disabled}
                        aria-current={String(it.id) === activeItemId ? "page" : undefined}
                        onClick={() => {
                          if (disabled) return
                          onNavigate(it)
                          setOpenGroup(g.id) // 选中落位:该组在 rail 上原地展开
                          leaveGroup()
                        }}
                      >
                        <span>{pickI18n(it.labelI18n, locale, it.name)}</span>
                        {disabled && <span className="nv-off">{offLabel}</span>}
                      </button>
                    )
                  }} />
                </div>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

const AppSidebar = memo(function AppSidebar() {
  const { t } = useTranslation("nav")
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { signOut } = useAuthContext()
  const activeTab = useAppSelector((state) => state.navigation.activeTab)
  const menuTree = useAppSelector((state) => state.auth.menuTree)
  // 区分"菜单确实为空"和"菜单加载失败",两者空态兜底文案不同,不能只看
  // menuTree.length === 0 一概而论:
  // - loadUserMenus 成功返回 []:用户已经在某个 tenant 下(哪怕是走 onboarding
  //   内置码兜底落进的占位 tenant),但被分配的角色没有任何菜单权限——按后端
  //   契约(docs/handoff/2026-09-03-pending-organization-frontend.md「另一个
  //   维度」)这是"已归属但没配角色",要等管理员配置,不是"未加入组织"，
  //   绝不能引导去 access code 页(AppSidebar 只在 AUTHENTICATED 路由下渲染,
  //   NEEDS_ONBOARDING 用户根本到不了这里,所以这条分支的用户 userStatus
  //   必然已经是 active)。
  // - loadUserMenus 失败(error 非空;authSlice 刻意不清空旧 menuTree,但首次
  //   加载就失败时 tree 本来为空):真故障,给重试入口。
  const menuLoadError = useAppSelector((state) => state.auth.error)
  // 菜单请求在途(loadUserMenus.pending 置 isLoading=true、error=null)时 menuTree
  // 还是初始 [] —— 这一瞬不能落进任何一种空态文案:prod 前置 review(2026-09-03)
  // 发现每个正常用户每次刷新都会先闪一下「等待管理员配置权限」。在途期间什么都不画。
  const menuLoading = useAppSelector((state) => state.auth.isLoading)
  const menuEmptyKind = resolveMenuEmptyKind(menuTree, menuLoading, menuLoadError)
  const locale = useLocale()
  const currentUserId = useAppSelector((s) => s.user.userId) ?? ''
  const { isPinned, canPinMore, togglePin, pinnedIds } = usePinnedNav(currentUserId)

  // 菜单 badge:仅当后端下发的菜单里确实有 /my-tasks 项才轮询计数
  // (菜单即权限 —— 没有该项的用户不该为它发请求吃 403)
  const hasMyTasksMenu = useMemo(() => {
    const walk = (items?: MenuItem[]): boolean =>
      !!items?.some(it =>
        (it.routePath && normalizePath(it.routePath) === MY_TASKS_BADGE_KEY) ||
        walk(it.children))
    return walk(menuTree ?? undefined)
  }, [menuTree])
  // 同 my-tasks:仅当后端菜单里有 /call-logs 项才轮询语音留言计数
  const hasCallLogsMenu = useMemo(() => {
    const walk = (items?: MenuItem[]): boolean =>
      !!items?.some(it =>
        (it.routePath && normalizePath(it.routePath) === VOICEMAIL_BADGE_KEY) ||
        walk(it.children))
    return walk(menuTree ?? undefined)
  }, [menuTree])
  // 同上:菜单里有 /owner-activity 才读业主留言未读数
  const hasOwnerActivityMenu = useMemo(() => {
    const walk = (items?: MenuItem[]): boolean =>
      !!items?.some(it =>
        (it.routePath && normalizePath(it.routePath) === OWNER_ACTIVITY_BADGE_KEY) ||
        walk(it.children))
    return walk(menuTree ?? undefined)
  }, [menuTree])
  const menuBadges = useMenuBadges(
    hasMyTasksMenu,
    hasCallLogsMenu,
    hasOwnerActivityMenu,
  )

  // 整栏收起状态沿用 SidebarProvider(localStorage "sidebar:state" + Ctrl+B 快捷键)
  // 手机（< 768，SidebarProvider 的 isMobile）：侧栏不占位、默认隐藏，由顶栏菜单按钮以抽屉形式打开（openMobile）；
  // 抽屉里永远是展开态导航。桌面沿用 open / collapsed（rail）。
  const { open, setOpen, isMobile, openMobile, setOpenMobile } = useSidebar()
  const collapsed = isMobile ? false : !open

  // 抽屉：切换路由即关闭（点菜单项导航后自动收起）；Esc 关闭
  useEffect(() => {
    if (isMobile) setOpenMobile(false)
  }, [location.pathname, location.search, isMobile, setOpenMobile])
  useEffect(() => {
    if (!isMobile || !openMobile) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpenMobile(false)
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [isMobile, openMobile, setOpenMobile])

  const [q, setQ] = useState("")
  // 展开态状态:钉住组开关 + 滚动区唯一展开组(单组手风琴)
  const [pinnedOpen, setPinnedOpen] = useState<boolean>(loadPinnedOpen)
  const [openGroupId, setOpenGroupId] = useState<string | null>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const query = q.trim().toLowerCase()
  const searching = query.length > 0

  const groups = useMemo(() => deriveNavGroups(menuTree), [menuTree])
  // 第一组(My Workspace)钉住在搜索框下、滚动区外;其余进滚动区
  const pinnedGroup = groups.length > 0 && groups[0].label ? groups[0] : null
  const restGroups = useMemo(
    () => (pinnedGroup ? groups.slice(1) : groups),
    [groups, pinnedGroup],
  )
  // pin 镜像:从非 Workspace 分组按 id 解析回 MenuItem(渲染层注入,不污染 groups/选中态)
  const pinnedItems = useMemo(
    () => resolvePinnedItems(restGroups, pinnedIds),
    [restGroups, pinnedIds],
  )
  // 收起 rail 态:把镜像追加进 Workspace 组(纯快捷导航,rail 内不放 pin 控件)
  const railGroups = useMemo(() => {
    if (!pinnedGroup || pinnedItems.length === 0) return groups
    return groups.map((g) =>
      g.id === pinnedGroup.id ? { ...g, items: [...g.items, ...pinnedItems], tree: [...(g.tree ?? g.items), ...pinnedItems] } : g,
    )
  }, [groups, pinnedGroup, pinnedItems])
  const visiblePinned = useMemo(
    () => (pinnedGroup ? (filterNavGroups([pinnedGroup], query)[0] ?? null) : null),
    [pinnedGroup, query],
  )
  const visibleRest = useMemo(() => filterNavGroups(restGroups, query), [restGroups, query])
  const activeItemId = useMemo(
    () => findActiveItemId(groups, location.pathname),
    [groups, location.pathname],
  )
  const activeGroupId = useMemo(
    () => findActiveGroupId(groups, location.pathname),
    [groups, location.pathname],
  )

  const togglePinned = useCallback(() => {
    setPinnedOpen((prev) => {
      const next = !prev
      try {
        localStorage.setItem(PINNED_OPEN_KEY, String(next))
      } catch {
        // localStorage 不可用时静默降级为本会话状态
      }
      return next
    })
  }, [])

  // 任何导航落位(侧栏点选或外部跳转)→ 只开当前页所在组
  useEffect(() => {
    setOpenGroupId(activeGroupId)
  }, [activeItemId, activeGroupId])

  const handleLogout = useCallback(async () => {
    try {
      await signOut()
      void navigate("/login")
    } catch (error) {
      console.error("Logout failed:", error)
      void navigate("/login")
    }
  }, [signOut, navigate])

  const navigateToItem = useCallback(
    (item: MenuItem) => {
      if (item.routePath === "/logout" || item.name.toLowerCase() === "logout") {
        void handleLogout()
        return
      }
      if (!item.routePath) return
      dispatch(setActiveTab(item.id.toString()))
      void navigate(normalizePath(item.routePath))
    },
    [dispatch, navigate, handleLogout],
  )

  // rail 搜索按钮：展开整栏并聚焦搜索框（等宽度过渡结束）
  const expandAndFocusSearch = useCallback(() => {
    setOpen(true)
    setTimeout(() => searchInputRef.current?.focus(), 250)
  }, [setOpen])

  // 路由变化时同步 Redux activeTab（页面刷新/直链进入也能高亮正确菜单）
  useEffect(() => {
    if (activeItemId && activeItemId !== activeTab) {
      dispatch(setActiveTab(activeItemId))
    }
  }, [activeItemId, activeTab, dispatch])

  // 展开态点选:跳转(落位由 activeItemId effect 收口到当前页所在组)
  const pickItem = useCallback(
    (item: MenuItem) => {
      setOpenGroupId(findActiveGroupId(groups, normalizePath(item.routePath || '')))
      navigateToItem(item)
    },
    [navigateToItem, groups],
  )

  const renderItem = (it: MenuItem, pinnable: boolean, showIcon = false) => {
    const ItemIcon = getIconComponent(it.icon, it.routePath)
    const disabled = it.isActive === false
    const badge = it.routePath ? menuBadges[normalizePath(it.routePath)] : undefined
    const pinned = isPinned(it.id)
    return (
      <div key={it.id} className="nv-item-row">
        <button
          type="button"
          className={showIcon ? "nv-item nv-top-item" : "nv-item"}
          data-active={String(it.id) === activeItemId}
          data-disabled={disabled}
          disabled={disabled}
          aria-current={String(it.id) === activeItemId ? "page" : undefined}
          onClick={() => !disabled && pickItem(it)}
        >
          {showIcon && <ItemIcon size={20} strokeWidth={1.7} />}
          <Highlight text={pickI18n(it.labelI18n, locale, it.name)} query={query} />
          {badge != null && badge > 0 && (
            <span className="nv-badge">{badge > 99 ? '99+' : badge}</span>
          )}
          {disabled && <span className="nv-off">{t("sidebar.off")}</span>}
        </button>
        {pinnable && !disabled && (
          <button
            type="button"
            className="nv-pinbtn"
            data-pinned={pinned}
            disabled={!pinned && !canPinMore}
            title={pinned ? t("sidebar.unpin") : canPinMore ? t("sidebar.pin") : t("sidebar.pinLimit")}
            aria-label={pinned ? t("sidebar.unpin") : t("sidebar.pin")}
            onClick={(e) => {
              e.stopPropagation()
              togglePin(it.id)
            }}
          >
            {pinned ? <PinOff size={13} strokeWidth={1.7} /> : <Pin size={13} strokeWidth={1.7} />}
          </button>
        )}
      </div>
    )
  }

  // 展开态:固定 My Workspace,其余菜单在滚动区域按内容高度排列。
  const renderExpandedNav = () => {
    if (!menuTree || menuTree.length === 0) {
      // 请求在途:既不是"失败"也不是"确实为空",留白等结果(见 menuLoading 声明处)。
      if (menuEmptyKind === "loading") {
        return <nav className="nv-scroll" data-testid="nv-menu-loading" />
      }
      // 空态分两种,文案不能共用(见上面 menuLoadError 声明处的完整判据说明):
      // - 菜单加载真失败(error 非空):给重试入口,不让用户只能刷新页面。
      // - 加载成功但确实是空(已归属但没配角色,等管理员授权):不能弹 access
      //   code 入口——这个用户已经在正确的 tenant 里了,输码解决不了问题。
      if (menuEmptyKind === "error") {
        return (
          <nav className="nv-scroll">
            <div className="nv-menu-empty">
              <span>{t("sidebar.menuUnavailable")}</span>
              <button
                type="button"
                onClick={() => {
                  void dispatch(loadUserMenus())
                }}
                aria-label={t("sidebar.retryLoadingMenu")}
              >
                <RefreshCw size={14} />
                <span>{t("sidebar.retry")}</span>
              </button>
            </div>
          </nav>
        )
      }
      return (
        <nav className="nv-scroll">
          <div className="nv-menu-empty">
            <Clock size={16} />
            <span>{t("sidebar.pendingRoleAssignment")}</span>
          </div>
        </nav>
      )
    }

    const pinnedIsOpen = searching || pinnedOpen
    const PinnedIcon = pinnedGroup ? getGroupIconComponent(pinnedGroup.label, pinnedGroup.icon) : null

    return (
      <>
        {visiblePinned && (!searching || visiblePinned.items.length > 0) && (
          <div className="nv-pin">
            <button
              type="button"
              className="nv-head"
              data-open={pinnedIsOpen}
              onClick={() => {
                if (!searching) togglePinned()
              }}
              aria-expanded={pinnedIsOpen}
            >
              <span className="nv-head-slot">
                <span className="nv-chev" style={{ transform: pinnedIsOpen ? "rotate(90deg)" : "none" }}>
                  <ChevronRight size={16} strokeWidth={1.7} />
                </span>
                {PinnedIcon && (
                  <span className="nv-gico">
                    <PinnedIcon size={20} strokeWidth={1.7} />
                  </span>
                )}
              </span>
              <span className="nv-label">{pickI18n(visiblePinned.labelI18n, locale, visiblePinned.label)}</span>
              <span className="nv-pinflag">{t("sidebar.pinned")}</span>
            </button>
            <Collapse open={pinnedIsOpen}>
              <div className="nv-items">
                <MenuBranches items={visiblePinned.tree ?? visiblePinned.items} activeItemId={activeItemId} searching={searching} renderLeaf={(it) => renderItem(it, false)} />
                {!searching && pinnedItems.map((it) => renderItem(it, true))}
              </div>
            </Collapse>
          </div>
        )}
        <nav className="nv-scroll">
          {visibleRest.map((g) => {
            const open = !g.label || isGroupOpen(g.id, openGroupId, searching)
            const GroupIcon = getGroupIconComponent(g.label, g.icon)
            // 组级 badge 汇总:组内任意子项(voicemail/tasks)有计数就加总,
            // 收起态也能看到组内待处理总数。
            const groupBadgeCount = sumGroupBadges(g.items, menuBadges)
            return (
              <React.Fragment key={g.id}>
                {g.label && (
                  <button
                    type="button"
                    className="nv-head"
                    data-open={open}
                    onClick={() => {
                      if (!searching) setOpenGroupId((prev) => reduceHeadClick(prev, g.id))
                    }}
                    aria-expanded={open}
                  >
                    <span className="nv-head-slot">
                      <span className="nv-chev" style={{ transform: open ? "rotate(90deg)" : "none" }}>
                        <ChevronRight size={16} strokeWidth={1.7} />
                      </span>
                      <span className="nv-gico">
                        <GroupIcon size={20} strokeWidth={1.7} />
                      </span>
                    </span>
                    <span className="nv-label">{pickI18n(g.labelI18n, locale, g.label)}</span>
                    {groupBadgeCount > 0 && (
                      <span className="nv-group-badge">
                        {groupBadgeCount > 99 ? "99+" : groupBadgeCount}
                      </span>
                    )}
                    {g.adminOnly && <span className="nv-admin">{t("sidebar.admin")}</span>}
                    {showActiveDot(g.id, openGroupId, activeGroupId, searching) && (
                      // active 组被关掉:琥珀小圆点提示「当前选中项在这组里」
                      <span className="nv-v5dot" />
                    )}
                  </button>
                )}
                <Collapse open={open}>
                  <div className="nv-items" data-root={!g.label}><MenuBranches items={g.tree ?? g.items} activeItemId={activeItemId} searching={searching} renderLeaf={(it) => renderItem(it, true, !g.label)} /></div>
                </Collapse>
              </React.Fragment>
            )
          })}
          {searching &&
            visibleRest.length === 0 &&
            (!visiblePinned || visiblePinned.items.length === 0) && (
              <div className="nv-empty">{t("sidebar.noMatches", { query: q.trim() })}</div>
            )}
        </nav>
      </>
    )
  }

  return (
    <>
      {/* 文档流占位:fixed 侧栏不占空间,由它把主内容推开;手机抽屉不占位 */}
      {!isMobile && <div className="nv-sb-gap" data-collapsed={collapsed} aria-hidden="true" />}
      {isMobile && <div className="nv-sb-backdrop" data-open={openMobile} aria-hidden="true" onClick={() => setOpenMobile(false)} />}
      <aside
        className="nv-sb"
        data-collapsed={collapsed}
        data-mobile={isMobile || undefined}
        data-open={isMobile ? openMobile : undefined}
        aria-hidden={isMobile && !openMobile ? true : undefined}
        inert={isMobile && !openMobile ? true : undefined}
      >
      {/* 品牌行 */}
      <div className="nv-brand">
        <img
          src="/nova-icon.png"
          alt="Nova"
          className="nv-logo"
          style={collapsed ? { cursor: "pointer" } : undefined}
          onClick={collapsed ? () => setOpen(true) : undefined}
        />
        {!collapsed && (
          <div className="nv-brand-text">
            <span className="nv-brand-name">Nova Vacation</span>
            <span className="nv-brand-version">v{__APP_VERSION__} · build {__BUILD_NUMBER__}</span>
          </div>
        )}
        {!collapsed && (
          <button
            type="button"
            className="nv-collapse"
            title={isMobile ? t("sidebar.closeMenu") : t("sidebar.collapseSidebar")}
            onClick={() => (isMobile ? setOpenMobile(false) : setOpen(false))}
          >
            <ChevronsLeft size={18} strokeWidth={2} />
          </button>
        )}
      </div>

      {collapsed ? (
        // 在途期间(loading)不进空态:走下面的 Rail 画一条空轨,等结果回来再定
        menuEmptyKind !== null && menuEmptyKind !== "loading" ? (
          // 空态兜底折叠版:与展开态 renderExpandedNav 判据一致(见
          // menuLoadError 声明处的完整说明)。菜单加载真失败给重试入口;
          // 加载成功但确实为空(已归属但没配角色)只给一个不可点的提示图标——
          // 没有可执行的动作,不能弹按钮误导用户去点什么。
          <div className="nv-rail">
            <button
              type="button"
              className="nv-rail-btn"
              data-tip={t("sidebar.expandSidebar")}
              onClick={() => setOpen(true)}
            >
              <ChevronsRight size={17} strokeWidth={2} />
            </button>
            {menuEmptyKind === "error" ? (
              <button
                type="button"
                className="nv-rail-btn"
                data-tip={t("sidebar.retryLoadingMenu")}
                aria-label={t("sidebar.retryLoadingMenu")}
                onClick={() => {
                  void dispatch(loadUserMenus())
                }}
              >
                <RefreshCw size={16} strokeWidth={2} />
              </button>
            ) : (
              <div
                className="nv-rail-btn"
                style={{ cursor: "default" }}
                data-tip={t("sidebar.pendingRoleAssignment")}
                role="status"
                aria-label={t("sidebar.pendingRoleAssignment")}
              >
                <Clock size={16} strokeWidth={2} />
              </div>
            )}
          </div>
        ) : (
          <Rail
            // 用 railGroups:在完整菜单基础上把 pin 镜像追加进 Workspace 组
            groups={railGroups}
            activeItemId={activeItemId}
            activeGroupId={activeGroupId}
            onNavigate={navigateToItem}
            onExpand={() => setOpen(true)}
            onSearch={expandAndFocusSearch}
            expandTip={t("sidebar.expandSidebar")}
            searchTip={t("sidebar.searchMenu")}
            adminLabel={t("sidebar.admin")}
            offLabel={t("sidebar.off")}
          />
        )
      ) : (
        <>
          <SearchBox
            q={q}
            setQ={setQ}
            inputRef={searchInputRef}
            placeholder={t("sidebar.searchPlaceholder")}
            ariaLabel={t("sidebar.searchAriaLabel")}
            clearTitle={t("sidebar.searchClear")}
          />
          {renderExpandedNav()}
        </>
      )}

      {/* 底部打卡 footer:展开态一键操作行 / 收起态 rail 时钟按钮(hover 飞出) */}
      <div className="nv-footer">
        <SidebarClockFooter collapsed={collapsed} />
      </div>
      </aside>
    </>
  )
})

export { AppSidebar }
